import React, { useCallback, useEffect, useState } from "react";
import { MapPin, Pencil, Building2, Radar } from "lucide-react";
import {
  GlobalButton,
  GlobalTextField,
} from "../../../../../global/forms/GlobalFormFields";
import { GlobalTableActionButton } from "../../../../../global/GlobalTableActionButton";
import { GlobalModal } from "../../../../../global/GlobalModal";
import PlaceSearchInput from "../PlaceSearchInput";
import AttendanceLocationMapPicker from "../AttendanceLocationMapPicker";
import {
  fetchAttendanceLocations,
  createAttendanceLocation,
  updateAttendanceLocation,
  deleteAttendanceLocation,
  reverseAttendancePlace,
} from "../../../api/attendanceApi";
import { formatShortAddress } from "../../../utils/formatAddress";

const DEFAULT_RADIUS = 100;

export default function AttendanceLocationsSection({ onNotify }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    id: null,
    location_label: "",
    place_name: "",
    latitude: null,
    longitude: null,
    radius_meters: DEFAULT_RADIUS,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchCityHint, setSearchCityHint] = useState("");

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAttendanceLocations();
      setRows(data);
    } catch (err) {
      onNotify?.(err.message, { variant: "error" });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [onNotify]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const resetForm = () => {
    setForm({
      id: null,
      location_label: "",
      place_name: "",
      latitude: null,
      longitude: null,
      radius_meters: DEFAULT_RADIUS,
    });
    setIsEditing(false);
    setFormError("");
  };

  const handlePlaceSelect = (place) => {
    setForm((prev) => ({
      ...prev,
      place_name: place.display_name,
      latitude: Number(place.latitude),
      longitude: Number(place.longitude),
    }));
    setFormError("");
  };

  const handleMapPosition = useCallback(async ({ lat, lng }) => {
    const latitude = Number(lat);
    const longitude = Number(lng);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

    setForm((prev) => ({
      ...prev,
      latitude,
      longitude,
    }));
    setFormError("");

    try {
      const place = await reverseAttendancePlace(latitude, longitude);
      if (place?.display_name) {
        setForm((prev) => ({ ...prev, place_name: place.display_name }));
      }
      if (place?.city_hint) {
        setSearchCityHint(place.city_hint);
      }
    } catch {
      /* pin is set */
    }
  }, []);

  const handleEdit = (row) => {
    setForm({
      id: row.id,
      location_label: row.location_label,
      place_name: row.place_name,
      latitude: row.latitude,
      longitude: row.longitude,
      radius_meters: row.radius_meters ?? DEFAULT_RADIUS,
    });
    setIsEditing(true);
    setFormError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    setFormError("");

    const location_label = String(form.location_label ?? "").trim();
    const place_name = String(form.place_name ?? "").trim();
    const radius = Number(form.radius_meters ?? DEFAULT_RADIUS);

    if (!location_label) {
      setFormError("Give this premises a name (e.g. Office, Training ground).");
      return;
    }
    if (!place_name || form.latitude == null || form.longitude == null) {
      setFormError("Search for a place, use My location, or click on the map to set the pin.");
      return;
    }

    const payload = {
      location_label,
      place_name,
      latitude: form.latitude,
      longitude: form.longitude,
      radius_meters: radius,
    };

    setSaving(true);
    try {
      if (isEditing && form.id) {
        await updateAttendanceLocation(form.id, payload);
        onNotify?.("Location updated successfully.", { variant: "success" });
      } else {
        await createAttendanceLocation(payload);
        onNotify?.("Location saved successfully.", { variant: "success" });
      }
      resetForm();
      loadRows();
    } catch (err) {
      setFormError(err.message || "Failed to save location.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAttendanceLocation(deleteTarget.id);
      onNotify?.("Location removed.", { variant: "success" });
      if (form.id === deleteTarget.id) resetForm();
      setDeleteTarget(null);
      loadRows();
    } catch (err) {
      onNotify?.(err.message, { variant: "error" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section id="att-locations" className="att-panel att-panel--locations">
      <div className="att-panel__head">
        <div className="att-panel__icon" aria-hidden>
          <MapPin size={20} />
        </div>
        <div>
          <h3 className="att-panel__title">Attendance premises</h3>
          <p className="att-panel__hint">
            Pin your site on the map, name it, and set how close staff must be to mark attendance.
          </p>
        </div>
      </div>

      <div className="att-panel__body att-panel__body--locations">
        <AttendanceLocationMapPicker
          latitude={form.latitude}
          longitude={form.longitude}
          radiusMeters={form.radius_meters ?? DEFAULT_RADIUS}
          onPositionChange={handleMapPosition}
          autoLocate={!isEditing && form.latitude == null}
        />

        <div className="att-location-form-card">
          <div className="att-location-form-card__head">
            <h4>{isEditing ? "Edit premises" : "New premises"}</h4>
            <p>Step 2 — name the site and confirm the address</p>
          </div>

          {formError ? (
            <div className="att-form-alert" role="alert">
              {formError}
            </div>
          ) : null}

          <div className="att-location-form-grid">
            <GlobalTextField
              label="Your name for this premises"
              value={form.location_label}
              onChange={(v) => setForm((p) => ({ ...p, location_label: v }))}
              placeholder="e.g. Office, Main ground"
              required
            />

            <PlaceSearchInput
              label="Search actual location"
              value={form.place_name}
              onChange={(v) => setForm((p) => ({ ...p, place_name: v }))}
              onPlaceSelect={handlePlaceSelect}
              disabled={saving}
              nearLat={form.latitude}
              nearLng={form.longitude}
              cityHint={searchCityHint}
            />

            <div className="att-radius-field">
              <label className="gff-label" htmlFor="att-radius">
                Check-in radius
              </label>
              <div className="att-radius-slider">
                <Radar size={16} aria-hidden className="att-radius-slider__icon" />
                <input
                  id="att-radius"
                  type="range"
                  min={10}
                  max={500}
                  step={10}
                  value={form.radius_meters ?? DEFAULT_RADIUS}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, radius_meters: Number(e.target.value) }))
                  }
                />
                <span className="att-radius-slider__value">
                  {form.radius_meters ?? DEFAULT_RADIUS} m
                </span>
              </div>
            </div>
          </div>

          <div className="att-location-form-card__actions">
            {isEditing ? (
              <GlobalButton type="button" variant="ghost" onClick={resetForm} disabled={saving}>
                Cancel
              </GlobalButton>
            ) : null}
            <GlobalButton type="button" variant="primary" onClick={handleSubmit} disabled={saving}>
              {saving ? "Saving…" : isEditing ? "Update premises" : "Save premises"}
            </GlobalButton>
          </div>
        </div>

        <div className="att-saved-section">
          <div className="att-saved-section__head">
            <h4>Saved premises</h4>
            <span className="att-saved-section__count">
              {loading ? "…" : rows.length}
            </span>
          </div>

          {loading ? (
            <p className="att-empty">Loading premises…</p>
          ) : rows.length === 0 ? (
            <div className="att-empty att-empty--boxed">
              <Building2 size={28} strokeWidth={1.5} aria-hidden />
              <p>No premises yet. Use the map above and save your first site.</p>
            </div>
          ) : (
            <ul className="att-saved-list">
              {rows.map((row) => (
                <li key={row.id} className="att-saved-item">
                  <div className="att-saved-item__body">
                    <div className="att-saved-item__top">
                      <h5>{row.location_label}</h5>
                      <span className="att-saved-item__zone">
                        {row.radius_meters ?? DEFAULT_RADIUS} m
                      </span>
                    </div>
                    <p title={row.place_name}>{formatShortAddress(row.place_name)}</p>
                    {row.latitude != null && row.longitude != null ? (
                      <span className="att-saved-item__coords">
                        {Number(row.latitude).toFixed(4)}, {Number(row.longitude).toFixed(4)}
                      </span>
                    ) : null}
                  </div>
                  <div className="att-saved-item__actions">
                    <GlobalTableActionButton action="edit" onClick={() => handleEdit(row)} />
                    <GlobalTableActionButton action="delete" onClick={() => setDeleteTarget(row)} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <GlobalModal
        open={Boolean(deleteTarget)}
        title="Remove premises?"
        onClose={() => !deleting && setDeleteTarget(null)}
        footer={
          <>
            <GlobalButton
              type="button"
              variant="ghost"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              Cancel
            </GlobalButton>
            <GlobalButton type="button" variant="danger" onClick={confirmDelete} disabled={deleting}>
              {deleting ? "Removing…" : "Remove"}
            </GlobalButton>
          </>
        }
      >
        <p>
          Remove <strong>{deleteTarget?.location_label}</strong>? Staff will no longer check in
          against this premises.
        </p>
      </GlobalModal>
    </section>
  );
}
